import { useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CONTACT_KIND_LABELS, type Contact } from '@kontor/shared';
import { api, ApiError } from '../../api';
import { useSession } from '../../session';
import { ContactDuplicates } from './ContactDuplicates';
import { ContactInquiries } from './ContactInquiries';
import { ContactPeople } from './ContactPeople';
import { RelatedOffers } from '../offers';
import { RelatedOrders } from '../orders';
import { FollowUpPanel } from '../crm';
import { RelationBadges } from './relationTypes';
import { ContactAccountButtons, ContactFinance } from '../finance';

const dateTime = new Intl.DateTimeFormat('de-DE', { dateStyle: 'long', timeStyle: 'short' });

export function ContactDetailPage() {
  const { id } = useParams();
  const { can, hasModule } = useSession();
  const client = useQueryClient();
  const [financeOpen, setFinanceOpen] = useState(false);
  const financeRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  // Nach dem Zusammenführen kommt man mit einer Meldung hierher
  const merged = (useLocation().state as { merged?: { name?: string; inquiries: number; people: number } } | null)?.merged;

  const contact = useQuery({ queryKey: ['contact', id], queryFn: async () => (await api<{ data: Contact }>('GET', `/contacts/${id}`)).data });
  const companyId = contact.data?.companyId ?? null;
  const company = useQuery({
    queryKey: ['contact', companyId],
    queryFn: async () => (await api<{ data: Contact }>('GET', `/contacts/${companyId}`)).data,
    enabled: !!companyId,
    retry: false,
  });
  const remove = useMutation({
    mutationFn: () => api('DELETE', `/contacts/${id}`),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['contacts'] });
      client.removeQueries({ queryKey: ['contact', id] });
      navigate('/kontakte', { replace: true });
    },
  });
  const archive = useMutation({
    mutationFn: () => api('POST', `/contacts/${id}/archive`),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['contacts'] });
      client.removeQueries({ queryKey: ['contact', id] });
      navigate('/kontakte', { replace: true });
    },
  });
  const restore = useMutation({
    mutationFn: () => api('POST', `/contacts/${id}/restore`),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['contacts'] });
      client.invalidateQueries({ queryKey: ['contact', id] });
    },
  });

  if (contact.isLoading) return <p className="muted">Wird geladen …</p>;
  if (contact.isError || !contact.data) {
    return (
      <div className="card narrow">
        <h1>Kontakt nicht gefunden</h1>
        <Link to="/kontakte">Zur Übersicht</Link>
      </div>
    );
  }
  const c = contact.data;
  const address = [c.street, [c.postalCode, c.city].filter(Boolean).join(' ')].filter(Boolean);

  return (
    <>
      <p><Link to="/kontakte" className="back-link">← Alle Kontakte</Link></p>
      <div className="page-head">
        <h1>{c.name}</h1>
        <span className="badge">{CONTACT_KIND_LABELS[c.kind]}</span>
        {can('contacts.write') && <Link className="btn btn--secondary btn--sm page-head__action" to={`/kontakte/${c.id}/bearbeiten`}>Bearbeiten</Link>}
        {can('contacts.write') && <Link className="btn btn--secondary btn--sm" to={`/kontakte/${c.id}/zusammenfuehren`}>Zusammenführen</Link>}
      </div>
      <p className="muted">Angelegt am {dateTime.format(new Date(c.createdAt))}.</p>
      <div className="contact-quick" role="group" aria-label="Schnellaktionen">
        {c.phone ? (
          <a className="btn btn--secondary" href={`tel:${c.phone.replace(/[^+\d]/g, '')}`} title={c.phone}>
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1 1 .4 1.9.7 2.8a2 2 0 0 1-.5 2.1L8.1 9.9a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.4c.9.3 1.8.6 2.8.7a2 2 0 0 1 1.7 2z" /></svg>
            Anrufen
          </a>
        ) : (
          <button type="button" className="btn btn--secondary" disabled title="Keine Telefonnummer hinterlegt">Anrufen</button>
        )}
        {c.email ? (
          <a className="btn btn--secondary" href={`mailto:${c.email}`} title={c.email}>
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="2" y="4" width="20" height="16" rx="2" /><path d="m22 7-10 6L2 7" /></svg>
            E-Mail schreiben
          </a>
        ) : (
          <button type="button" className="btn btn--secondary" disabled title="Keine E-Mail-Adresse hinterlegt">E-Mail schreiben</button>
        )}
        <ContactAccountButtons contact={c} />
      </div>
      {merged && (
        <div className="alert alert--ok" role="status">
          „{merged.name}“ ist in diesen Kontakt aufgegangen.
          {merged.inquiries > 0 && ` ${merged.inquiries} ${merged.inquiries === 1 ? 'Anfrage ist' : 'Anfragen sind'} hierher übergegangen.`}
          {merged.people > 0 && ` ${merged.people} ${merged.people === 1 ? 'Ansprechpartner ist' : 'Ansprechpartner sind'} hierher übergegangen.`}
        </div>
      )}

      <div className="detail-grid">
        <section className="card">
          <h2>Kontaktdaten</h2>
          <dl className="facts">
            <dt>Beziehung</dt>
            <dd>{c.relationTypeIds.length > 0 ? <RelationBadges ids={c.relationTypeIds} /> : <span className="muted">–</span>}</dd>
            {c.kind === 'person' && (
              <>
                <dt>Firma</dt>
                <dd>
                  {!c.companyId && <span className="muted">Keine (Privatperson)</span>}
                  {c.companyId && company.data && <Link to={`/kontakte/${company.data.id}`} className="strong-link">{company.data.name}</Link>}
                  {c.companyId && company.isLoading && <span className="muted">Wird geladen …</span>}
                  {c.companyId && company.isError && <span className="muted">Die Firma ist nicht mehr vorhanden.</span>}
                </dd>
              </>
            )}
            <dt>E-Mail</dt>
            <dd>{c.email ? <a href={`mailto:${c.email}`}>{c.email}</a> : <span className="muted">–</span>}</dd>
            <dt>Telefon</dt>
            <dd>{c.phone ? <a href={`tel:${c.phone.replace(/[^+\d]/g, '')}`}>{c.phone}</a> : <span className="muted">–</span>}</dd>
            <dt>Adresse</dt>
            <dd>{address.length > 0 ? address.map((line) => <div key={line}>{line}</div>) : <span className="muted">–</span>}</dd>
          </dl>
        </section>

        <section className="card">
          <h2>Interne Notiz</h2>
          {c.note ? <p className="message">{c.note}</p> : <p className="muted">Keine Notiz.</p>}

          {(can('contacts.write') || can('contacts.delete')) && (
            <div className="danger-zone">
              {c.archived && <div className="alert alert--warn" style={{ flexBasis: "100%" }} role="status">Dieser Kontakt ist archiviert. Er steht in keiner Liste und Auswahl, seine Belege bleiben unverändert.</div>}
              {[remove, archive, restore].map((m) => m.error instanceof ApiError && <div key={m.submittedAt} className="alert alert--error" style={{ flexBasis: "100%" }} role="alert">{m.error.message}</div>)}
              <div className="form-actions">
                {can('contacts.write') && c.archived && (
                  <button className="btn btn--secondary btn--sm" type="button" disabled={restore.isPending} onClick={() => restore.mutate()}>Wiederherstellen</button>
                )}
                {can('contacts.write') && !c.archived && (
                  <button
                    className="btn btn--secondary btn--sm"
                    type="button"
                    disabled={archive.isPending}
                    title="Blendet den Kontakt aus Listen und Auswahlen aus. Nichts wird gelöscht."
                    onClick={() => {
                      if (window.confirm(`Kontakt „${c.name}“ archivieren? Er verschwindet aus den Listen, die Daten bleiben erhalten, und Sie können ihn jederzeit wiederherstellen.`)) archive.mutate();
                    }}
                  >
                    Archivieren
                  </button>
                )}
                {can('contacts.delete') && (
                  <button
                    className="btn btn--danger btn--sm"
                    type="button"
                    disabled={remove.isPending}
                    onClick={() => {
                      if (window.confirm(`Kontakt „${c.name}“ endgültig löschen? Der Kontakt wird mit seinem Debitor und Kreditor aus der Datenbank entfernt. Das lässt sich nicht rückgängig machen. Wenn Sie ihn nur ausblenden möchten, wählen Sie „Archivieren“.`)) remove.mutate();
                    }}
                  >
                    Endgültig löschen
                  </button>
                )}
              </div>
            </div>
          )}
        </section>
      </div>

      <ContactDuplicates contact={c} />
      <div ref={financeRef}><ContactFinance contact={c} open={financeOpen} onOpenChange={setFinanceOpen} /></div>
      {c.kind === 'company' && <ContactPeople company={c} />}
      <ContactInquiries contactId={c.id} />
      {hasModule('offers') && can('offers.read') && <RelatedOffers contactId={c.id} />}
      {hasModule('orders') && can('orders.read') && <RelatedOrders contactId={c.id} />}
      <FollowUpPanel subjectKind="contact" subjectId={c.id} />
    </>
  );
}
